import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const stats = [
  { label: "Total Revenue", value: "$284,500", change: "+12.4%", up: true, icon: "ri-money-dollar-circle-line" },
  { label: "Active Orders", value: "1,284", change: "+8.1%", up: true, icon: "ri-shopping-bag-3-line" },
  { label: "Products Listed", value: "342", change: "+5", up: true, icon: "ri-store-2-line" },
  { label: "Avg. Rating", value: "4.8", change: "+0.2", up: true, icon: "ri-star-line" },
];
const orders = [
  { id: "#CG-20481", buyer: "TechRetail Inc.", country: "🇺🇸 USA", product: "Wireless Earbuds Pro X200", qty: 500, amount: "$4,250", status: "Shipped", date: "Apr 8, 2026" },
  { id: "#CG-20480", buyer: "EcoWear London", country: "🇬🇧 UK", product: "Organic Cotton T-Shirt", qty: 1000, amount: "$2,800", status: "Processing", date: "Apr 7, 2026" },
  { id: "#CG-20479", buyer: "GreenPower Pty", country: "🇦🇺 AU", product: "Solar Panel 400W", qty: 20, amount: "$1,900", status: "Pending", date: "Apr 6, 2026" },
  { id: "#CG-20478", buyer: "Gulf Auto Trading", country: "🇦🇪 UAE", product: "Hydraulic Brake Pads", qty: 200, amount: "$2,400", status: "Delivered", date: "Apr 5, 2026" },
  { id: "#CG-20477", buyer: "BeautyNorth Inc.", country: "🇨🇦 CA", product: "Vitamin C Serum 30ml", qty: 300, amount: "$1,350", status: "Delivered", date: "Apr 4, 2026" },
];
const statusColors: Record<string, string> = {
  Shipped: "bg-blue-100 text-blue-600",
  Processing: "bg-amber-100 text-amber-600",
  Pending: "bg-gray-100 text-gray-600",
  Delivered: "bg-green-100 text-green-600",
};

export default function VendorDashboardPage() {
  useSEO({
    title: "Vendor Dashboard | Crown Global Factory & Buyer",
    description: "Manage your factory listings, orders, analytics, and membership on Crown Global.",
    canonical: "/vendor-dashboard",
  });

  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900">Vendor Dashboard</h1>
              <p className="text-gray-500 text-sm mt-1">Welcome back, Shenzhen Precision Tech Co.</p>
            </div>
            <button className="bg-amber-400 hover:bg-amber-500 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line mr-1"></i> Add Product
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((s) => (
              <div key={s.label} className="bg-white rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl">
                    <i className={`${s.icon} text-amber-500 text-xl`}></i>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.up ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>{s.change}</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Orders table */}
            <div className="lg:col-span-2 bg-white rounded-xl p-5">
              <h2 className="font-bold text-gray-800 mb-4">Recent Orders</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {["Order ID", "Buyer", "Product", "Qty", "Amount", "Status", "Date"].map((h) => (
                        <th key={h} className="text-left text-xs text-gray-400 font-semibold pb-3 pr-4 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 pr-4 text-amber-500 font-semibold whitespace-nowrap">{o.id}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <div className="font-medium text-gray-800">{o.buyer}</div>
                          <div className="text-xs text-gray-400">{o.country}</div>
                        </td>
                        <td className="py-3 pr-4 text-gray-600 max-w-[140px] truncate">{o.product}</td>
                        <td className="py-3 pr-4 text-gray-600 whitespace-nowrap">{o.qty}</td>
                        <td className="py-3 pr-4 font-semibold text-gray-800 whitespace-nowrap">{o.amount}</td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[o.status]}`}>{o.status}</span>
                        </td>
                        <td className="py-3 text-gray-400 text-xs whitespace-nowrap">{o.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex flex-col gap-4">
              <div className="bg-white rounded-xl p-5">
                <h2 className="font-bold text-gray-800 mb-4">Quick Actions</h2>
                <div className="flex flex-col gap-2">
                  {[
                    { icon: "ri-add-box-line", label: "Add New Product", color: "text-amber-500" },
                    { icon: "ri-image-add-line", label: "Upload Product Images", color: "text-blue-500" },
                    { icon: "ri-price-tag-3-line", label: "Manage Pricing", color: "text-green-500" },
                    { icon: "ri-truck-line", label: "Track Shipments", color: "text-purple-500" },
                    { icon: "ri-bar-chart-2-line", label: "View Analytics", color: "text-red-500" },
                    { icon: "ri-customer-service-2-line", label: "Contact Support", color: "text-gray-500" },
                  ].map((a) => (
                    <button key={a.label} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-left">
                      <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg shrink-0">
                        <i className={`${a.icon} ${a.color} text-base`}></i>
                      </div>
                      <span className="text-sm text-gray-700">{a.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-amber-400 rounded-xl p-5 text-white">
                <i className="ri-vip-crown-2-line text-3xl mb-2 block"></i>
                <h3 className="font-bold text-lg mb-1">Upgrade to Gold</h3>
                <p className="text-amber-100 text-sm mb-4">Get 10x more visibility and priority placement in search results. Only $26/month.</p>
                <button onClick={() => navigate("/membership")} className="bg-white text-amber-500 font-bold text-sm px-4 py-2 rounded-full cursor-pointer whitespace-nowrap hover:bg-amber-50 transition-colors">Upgrade Now — $26/mo</button>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl shrink-0">
                    <i className="ri-shield-star-line text-amber-500 text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">Basic Membership</p>
                    <p className="text-xs text-green-600 font-semibold flex items-center gap-1"><i className="ri-checkbox-circle-fill"></i> Active</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-3">Your current plan renews on May 10, 2026 · $10/month</p>
                <button onClick={() => navigate("/membership")} className="w-full text-xs font-semibold text-amber-500 border border-amber-200 py-2 rounded-full hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">Manage Membership</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
